import { Stack, TableCell, Tooltip, Typography } from '@mui/material';
import * as S from './ExamSessions.styled';
import { ExamSessionTableCellsPropsType } from './ExamSessions.types';
import { AnomaliesCell } from './components';
import { ActionMenu, CONTACT_PROCTORFREE_SUPPORT_AUDIT_BASE_URL, useToast } from '@shared';
import { MoreHoriz, ReportProblemOutlined } from '@mui/icons-material';

const statusColors: Record<string, string> = {
  Done: '#2e7d32',
  'No Auditing': '#0288d1',
  Processing: '#f57c00',
  Capturing: '#bdbdbd',
  Failed: '#d32f2f',
  'Secure Browser Only': '#607D8B',
};

export function ExamSessionTableCells({
  isShort,
  isRecordOnly,
  entries,
  isAuditOfInterest,
  isFailed,
  auditStatus,
  audiUuid,
}: ExamSessionTableCellsPropsType) {
  const toast = useToast();

  const openReportPage = (auditUuid: string) => {
    const auditUrl = `${window.location.origin}/audit/${auditUuid}`;
    const encodedAuditUrl = encodeURIComponent(auditUrl);

    const finalUrl = `${CONTACT_PROCTORFREE_SUPPORT_AUDIT_BASE_URL}?audit_url=${encodedAuditUrl}`;

    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const stopPropagation = (event: React.SyntheticEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const copyToClipboard = async (value: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleTooltipEmailClick = async (event: React.MouseEvent<HTMLElement>, email: string) => {
    stopPropagation(event);

    if (!email) {
      return;
    }

    try {
      await copyToClipboard(email);
      toast.success('Email copied');
    } catch {
      toast.error('Failed to copy email');
    }
  };

  return (
    <>
      {entries.map((entry, index) => {
        const [keyName, value] = entry;
        const isTime = keyName === 'examStartTime' || keyName === 'examEndTime';
        const isDuration = keyName === 'examDuration';
        const isAuditStatus = keyName === 'auditStatus';
        const isAnomaly = keyName === 'anomalyCount';
        const isEmail = keyName === 'userEmail';
        const computedAuditStatus = isFailed ? 'Failed' : auditStatus;
        const isNotLastCell = !(index === entries.length - 1);

        const cell = (
          <TableCell
            key={keyName}
            sx={
              isEmail
                ? { width: 120, maxWidth: 120, minWidth: 120, px: 1.5 }
                : isAnomaly
                ? { width: 92, maxWidth: 92, minWidth: 92, px: 1.5 }
                : isTime || isDuration || isAuditStatus
                ? { whiteSpace: 'nowrap' as const, px: 1.5 }
                : { px: 1.5 }
            }
          >
            {isDuration ? (
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component={S.TimeText} variant='body2'>
                  {value}
                </Typography>
                {isShort && <S.DurationBadge>Short</S.DurationBadge>}
                {isRecordOnly && <S.DurationBadge>Record Only</S.DurationBadge>}
              </Stack>
            ) : isAuditStatus ? (
              <S.StatusBadge color={statusColors[computedAuditStatus] ?? '#999'}>
                {computedAuditStatus}
              </S.StatusBadge>
            ) : (
              <>
                {isAuditOfInterest && isAnomaly ? (
                  <AnomaliesCell value={value} />
                ) : isEmail ? (
                  <Tooltip
                    title={
                      value ? (
                        <button
                          type='button'
                          onClick={(event) => handleTooltipEmailClick(event, String(value))}
                          onMouseDown={stopPropagation}
                          style={{
                            all: 'unset',
                            display: 'block',
                            userSelect: 'text',
                            cursor: 'copy',
                          }}
                        >
                          {String(value)}
                        </button>
                      ) : (
                        ''
                      )
                    }
                    disableHoverListener={!value}
                    componentsProps={{
                      tooltip: {
                        onClick: stopPropagation,
                        onMouseDown: stopPropagation,
                        sx: { pointerEvents: 'auto' },
                      },
                    }}
                  >
                    <Typography component={S.TruncatedEmail} variant='body2'>
                      {value}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography component={isTime ? S.TimeText : 'span'} variant='body2'>
                    {value}
                  </Typography>
                )}
              </>
            )}
          </TableCell>
        );

        if (isNotLastCell) {
          return cell;
        }

        return (
          <>
            {cell}
            {
              <TableCell
                key={'exam_session_table_action_button'}
                align='center'
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: 56,
                  maxWidth: 56,
                  minWidth: 56,
                  px: 0.5,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}
              >
                <ActionMenu
                  icon={MoreHoriz}
                  items={[
                    {
                      label: 'Report Audit',
                      icon: ReportProblemOutlined,
                      // disabled: auditStatus != ''
                      onClick: () => openReportPage(audiUuid),
                    },
                  ]}
                />
              </TableCell>
            }
          </>
        );
      })}
    </>
  );
}
